import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import {
  selectFilteredMcbimFloors,
  selectFloorSearchText,
  selectSelectedFloor,
  selectShowOnlyFloorsWithIssues,
} from "./floorplanSelectors";
import {
  setFloorSearchTextInRedux,
  setSelectedFloorInRedux,
  setShowOnlyFloorsWithIssuesInRedux,
} from "./floorplanSlice";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";

const FloorplanFloorList: React.FC = () => {
  const [showSearch, setShowSearch] = useState(false);
  const dispatch = useAppDispatch();
  const floors = useAppSelector(selectFilteredMcbimFloors);
  const selectedFloor = useAppSelector(selectSelectedFloor);
  const floorSearchText = useAppSelector(selectFloorSearchText);
  const showOnlyFloorsWithIssues = useAppSelector(selectShowOnlyFloorsWithIssues);
  const issueBoardId = useAppSelector(selectIssueBoardId);

  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      style={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Text fw={600}>Etasjer</Text>

        <Group gap="xs" wrap="nowrap">
          {showSearch && (
            <TextInput
              size="xs"
              placeholder="Søk etasje"
              value={floorSearchText}
              onChange={(event) => {
                if (issueBoardId == null) return;

                dispatch(setFloorSearchTextInRedux({ issueBoardId, floorSearchText: event.currentTarget.value }));
              }}
              style={{ width: 150 }}
            />
          )}

          <Tooltip label={showSearch ? "Skjul søk" : "Søk etasje"} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => {
                const nextShowSearch = !showSearch;
                setShowSearch(nextShowSearch);
                if (!nextShowSearch && issueBoardId != null) {
                  dispatch(setFloorSearchTextInRedux({ issueBoardId, floorSearchText: "" }));
                }
              }}
            >
              {showSearch ? <IconX size={16} /> : <IconSearch size={16} />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Paper radius="xl" px="md" py="xs" mb="sm">
        <Switch
          label="Vis bare etasjer med saker"
          checked={showOnlyFloorsWithIssues}
          color="teal"
          onChange={(event) => {
            if (issueBoardId == null) return;

            dispatch(
              setShowOnlyFloorsWithIssuesInRedux({
                issueBoardId,
                showOnlyFloorsWithIssues: event.currentTarget.checked,
              }),
            );
          }}
        />
      </Paper>

      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
        <Stack gap="xs">
          {floors.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Ingen etasjer matcher filteret.
            </Text>
          )}

          {floors.map((item) => (
            <Button
              key={item.globalId || item.floor}
              variant={selectedFloor === item.floor ? "filled" : "light"}
              color={selectedFloor === item.floor ? "mc-primary" : "gray"}
              fullWidth
              styles={{
                inner: {
                  width: "100%",
                  justifyContent: "space-between",
                },
                label: {
                  width: "100%",
                },
              }}
              onClick={() => {
                if (issueBoardId == null) return;

                dispatch(setSelectedFloorInRedux({ issueBoardId, selectedFloor: item.floor }));
              }}
            >
              <Group justify="space-between" wrap="nowrap" style={{ width: "100%" }}>
                <span>{item.floor}</span>
                {item.issueCount > 0 && (
                  <Tooltip label={`${item.issueCount} kopplade saker på våningen`} withArrow>
                    <Badge
                      variant={selectedFloor === item.floor ? "white" : "filled"}
                      color={selectedFloor === item.floor ? "dark" : "teal"}
                    >
                      {item.issueCount}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </Button>
          ))}
        </Stack>
      </ScrollArea>
    </Paper>
  );
};

export default FloorplanFloorList;
